import { ArrowLeftIcon, ArrowRightIcon, Button, DatePicker, DateRangePicker } from '@bp/ui-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useSelectedSchoolYear } from '../../hooks/useSelectedSchoolYear';
import { useSubstitutionDateRange } from '../../hooks/useSubstitutionDateRange';
import { getDayOfWeekAsString } from '../../utils/dateCalculations';
import { lowercaseFirstLetter } from '../../utils/stringHelper';
import { NavigationTabs } from '../NavigationTabs/NavigationTabs';
import { useSubstitutionStore } from '../Substitutions/SubstitutionProvider';
import styles from './SubstitutionsTopBar.module.scss';

type SubstitutionsTopBarProps = {
  baseSlug: string;
};

export const SubstitutionsTopBar = observer(({ baseSlug }: SubstitutionsTopBarProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const currentSchoolYear = useSelectedSchoolYear();

  const store = useSubstitutionStore();

  const isSchoolEventPath = location.pathname.endsWith(t('routes.schoolEvents.slug'));
  const isAbsencesPath = location.pathname.endsWith(t('routes.absences.slug'));
  // Veranstaltungen und Abwesenheiten zeigen einen Zeitraum, Vertretungen einen einzelnen Tag
  const showRange = isSchoolEventPath || isAbsencesPath;
  const range = useSubstitutionDateRange();

  // Schnellauswahl im Zeitraum-Picker, jeweils innerhalb des Schuljahres
  const schoolYearStart = dayjs(currentSchoolYear?.start).startOf('day');
  const schoolYearEnd = dayjs(currentSchoolYear?.end).startOf('day');
  const weekStart = dayjs().startOf('isoWeek');
  const weekEnd = dayjs().endOf('isoWeek').startOf('day');

  return (
    <div className={styles['substitutions-top-bar']}>
      <NavigationTabs
        tabs={[
          {
            title: t('schoolEvents.title', { count: 2, context: 'short' }),
            path: `/${baseSlug}/${t('routes.schoolEvents.slug')}`,
            active: isSchoolEventPath,
          },
          {
            title: t('absences.title', { count: 2 }),
            path: `/${baseSlug}/${t('routes.absences.slug')}`,
            active: isAbsencesPath,
          },
          {
            title: t('substitutions.title', { count: 2 }),
            path: `/${baseSlug}/${t('routes.substitutions.slug')}`,
            active: location.pathname.endsWith(t('routes.substitutions.slug')),
          },
        ]}
        size='xxl'
        hideBorder
        className={styles.tabs}
      />
      {showRange ? (
        <div className={styles.date}>
          <div className={styles.label}>{t('common.span')}</div>
          <DateRangePicker
            className={styles['range-picker']}
            name='range'
            dense
            start={range.from.toDate()}
            end={store.rangeEnd === null ? null : range.to.toDate()}
            minDate={dayjs(currentSchoolYear?.start).toDate()}
            maxDate={dayjs(currentSchoolYear?.end).toDate()}
            presets={[
              {
                label: t('common.wholeSchoolYear'),
                start: schoolYearStart.toDate(),
                end: schoolYearEnd.toDate(),
              },
              {
                label: t('common.thisWeek'),
                start: (weekStart.isBefore(schoolYearStart) ? schoolYearStart : weekStart).toDate(),
                end: (weekEnd.isAfter(schoolYearEnd) ? schoolYearEnd : weekEnd).toDate(),
              },
            ]}
            onChange={({ start, end }) => {
              store.setRange(start ? dayjs(start) : null, end ? dayjs(end) : null);
            }}
          />
        </div>
      ) : (
        <div className={styles.date}>
          <div className={styles.label}>
            {lowercaseFirstLetter(
              t('absences.for', {
                for: getDayOfWeekAsString(store.selectedDay),
              }),
            )}
          </div>
          <Button
            name='decrease'
            hierarchy='secondary'
            onClick={() => store.setSelectedDay(store.selectedDay.subtract(1, 'day'))}
            icon={<ArrowLeftIcon />}
          />
          <DatePicker
            className={styles.picker}
            onChange={(e) => {
              store.setSelectedDay(dayjs(e));
            }}
            name='date'
            value={store.selectedDay.toDate()}
            dense
            minDate={dayjs(currentSchoolYear?.start).toDate()}
            maxDate={dayjs(currentSchoolYear?.end).toDate()}
          />
          <Button
            name='increase'
            hierarchy='secondary'
            onClick={() => store.setSelectedDay(store.selectedDay.add(1, 'day'))}
            icon={<ArrowRightIcon />}
          />
        </div>
      )}
    </div>
  );
});
